import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import {
  featureCustomizations,
  features,
  useFeatureManagerStore,
  type FeatureCustomizationKey,
  type FeatureCustomizations,
  type FeatureFlags,
  type FeatureKey,
} from '@peanuds/shared-state';
import { appConfig } from './config';

export type {
  FeatureConfig,
  FeatureCustomizationKey,
  FeatureCustomizations,
  FeatureFlags,
  FeatureKey,
} from '@peanuds/shared-state';
export { featureCustomizations, features, useFeatureManagerStore };

const knownFeatureKeys = new Set<FeatureKey>(Object.values(features));
const knownFeatureCustomizationKeys = new Set<FeatureCustomizationKey>(
  Object.values(featureCustomizations),
);

export type FeatureConfigOverrides = {
  flags: FeatureFlags;
  customizations: FeatureCustomizations;
};

/**
 * Reactive access to the full feature flag map.
 */
export const useFeatureManager = () => {
  const store = useFeatureManagerStore();
  const { customizations, flags } = storeToRefs(store);
  const { getCustomization, isEnabled } = store;

  return {
    customizations,
    flags,
    getCustomization,
    isEnabled,
  };
};

/**
 * Reactive helper for template-friendly single-flag checks.
 */
export const useFeatureFlag = (key: FeatureKey, defaultValue = false) => {
  const { flags } = useFeatureManager();

  return computed(() => flags.value[key] ?? defaultValue);
};

export const useFeatureCustomization = (key: FeatureCustomizationKey, defaultValue = false) => {
  const { customizations } = useFeatureManager();

  return computed(() => customizations.value[key] ?? defaultValue);
};

/**
 * Parses deployment-time feature flag and customization overrides from appConfig.
 *
 * Expected format:
 * VITE_FEATURE_FLAGS='{"dashboard.totalBalanceCard":true,"customizations.beneficiaries.sepaBicVisible":false}'
 */
export const getFeatureConfigOverridesFromAppConfig = (
  config: Partial<ImportMetaEnv> = appConfig,
): FeatureConfigOverrides => {
  const rawFeatureFlags = config.VITE_FEATURE_FLAGS;

  if (!rawFeatureFlags) {
    return { flags: {}, customizations: {} };
  }

  try {
    const parsedFeatureFlags = JSON.parse(rawFeatureFlags);

    if (!parsedFeatureFlags || typeof parsedFeatureFlags !== 'object') {
      return { flags: {}, customizations: {} };
    }

    return Object.entries(parsedFeatureFlags).reduce<FeatureConfigOverrides>(
      (overrides, [key, value]) => {
        if (typeof value !== 'boolean') {
          return overrides;
        }

        if (knownFeatureKeys.has(key as FeatureKey)) {
          overrides.flags[key as FeatureKey] = value;
          return overrides;
        }

        if (knownFeatureCustomizationKeys.has(key as FeatureCustomizationKey)) {
          overrides.customizations[key as FeatureCustomizationKey] = value;
        }

        return overrides;
      },
      { flags: {}, customizations: {} },
    );
  } catch {
    return { flags: {}, customizations: {} };
  }
};

export const getFeatureFlagOverridesFromAppConfig = (
  config: Partial<ImportMetaEnv> = appConfig,
): FeatureFlags => getFeatureConfigOverridesFromAppConfig(config).flags;

export const getFeatureCustomizationOverridesFromAppConfig = (
  config: Partial<ImportMetaEnv> = appConfig,
): FeatureCustomizations => getFeatureConfigOverridesFromAppConfig(config).customizations;
